import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IPublicClientApplication } from '@azure/msal-browser';
import { AppTheme, Task, Project, TaskPriority, TaskStatus } from '../types';
import { dvList } from '../api/dataverseClient';
import { getAccount } from '../auth/authService';
import { listProjects } from '../services/projectService';
import {
  listMyMentions, listMyWatchedItems, markNotificationRead,
  MentionFeedItem, WatchedItem,
} from '../services/notificationService';

interface Props { pca: IPublicClientApplication; theme: AppTheme; }

// ── Constants ─────────────────────────────────────────────────────────

const TASK_FIELDS = [
  'dagrun_pm_taskid', 'dagrun_pm_name', 'dagrun_pm_status', 'dagrun_pm_priority',
  'dagrun_pm_assigneeid', 'dagrun_pm_assigneename',
  'dagrun_pm_duedate', 'dagrun_pm_startdate',
  '_dagrun_pm_projectid_value',
];

const PRIORITY_COLOR: Record<number, string> = {
  [TaskPriority.Critical]: '#DC2626',
  [TaskPriority.High]:     '#EA580C',
  [TaskPriority.Medium]:   '#D97706',
  [TaskPriority.Low]:      '#9198A8',
};

const STATUS_LABEL: Record<number, string> = {
  [TaskStatus.NotStarted]: 'Not Started',
  [TaskStatus.InProgress]: 'In Progress',
  [TaskStatus.InReview]:   'In Review',
  [TaskStatus.Done]:       'Done',
  [TaskStatus.OnHold]:     'On Hold',
  [TaskStatus.Cancelled]:  'Cancelled',
};

// Watch List type → colored chip background.
const TYPE_CHIP: Record<string, { bg: string; fg: string }> = {
  Task:          { bg: '#DBEAFE', fg: '#1E40AF' },
  Issue:         { bg: '#FED7AA', fg: '#9A3412' },
  Risk:          { bg: '#FECACA', fg: '#991B1B' },
  Decision:      { bg: '#E9D5FF', fg: '#6B21A8' },
  Milestone:     { bg: '#CCFBF1', fg: '#115E59' },
  ChangeRequest: { bg: '#FEF3C7', fg: '#92400E' },
  Unknown:       { bg: '#F3F4F6', fg: '#5A6272' },
};

// localStorage key prefix; one key per user OID so multi-account devices don't bleed.
const SECTIONS_KEY = (oid: string) => `dashboard_sections_${oid}`;

interface SectionsState {
  myTasks: boolean;     // true = collapsed
  myMentions: boolean;
  myWatchList: boolean;
}
const DEFAULT_SECTIONS: SectionsState = { myTasks: false, myMentions: false, myWatchList: false };

// ── Date helpers ──────────────────────────────────────────────────────

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
function relativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  const ms = Date.now() - t;
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ── Data classification ───────────────────────────────────────────────

type DueGroup = 'Overdue' | 'Due Today' | 'Due This Week' | 'Upcoming';

function classifyDue(t: Task, today: Date): DueGroup {
  if (!t.dagrun_pm_duedate) return 'Upcoming';
  const due = new Date(t.dagrun_pm_duedate);
  const dueDay = startOfDay(due);
  const todayDay = startOfDay(today);
  if (dueDay < todayDay) return 'Overdue';
  if (isSameDay(due, today)) return 'Due Today';
  const sevenFromNow = new Date(todayDay.getTime() + 7 * 86400000);
  if (dueDay <= sevenFromNow) return 'Due This Week';
  return 'Upcoming';
}

const DUE_GROUP_ORDER: DueGroup[] = ['Overdue', 'Due Today', 'Due This Week', 'Upcoming'];

// ── Section header component ──────────────────────────────────────────

interface SectionHeaderProps {
  title: string;
  count: number;
  collapsed: boolean;
  onToggle: () => void;
  rightSlot?: React.ReactNode;
}
function SectionHeader({ title, count, collapsed, onToggle, rightSlot }: SectionHeaderProps) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '14px 0 10px',
      borderBottom: '0.5px solid var(--border-light)',
      marginBottom: 12,
    }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 8,
          fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 300,
          color: 'var(--ink)', padding: 0,
        }}
      >
        <span style={{
          display: 'inline-block',
          transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)',
          transition: 'transform 0.15s',
          fontSize: 11, lineHeight: 1, color: 'var(--ink-3)',
        }}>▶</span>
        <span>{title}</span>
        <span style={{
          fontFamily: 'var(--font-mono)', fontSize: 11,
          background: 'var(--surface-2)', color: 'var(--ink-3)',
          padding: '2px 8px', borderRadius: 10,
        }}>{count}</span>
      </button>
      <div style={{ flex: 1 }} />
      {rightSlot}
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────

export default function MyDashboard({ pca }: Props) {
  const nav = useNavigate();

  const [userId, setUserId] = useState<string>('');
  const [sections, setSections] = useState<SectionsState>(DEFAULT_SECTIONS);

  const [tasks, setTasks] = useState<Task[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [mentions, setMentions] = useState<MentionFeedItem[]>([]);
  const [watched, setWatched] = useState<WatchedItem[]>([]);
  const [watchedTasks, setWatchedTasks] = useState<Map<string, Task>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Mentions filter state. Default to Unread so the list surfaces what
  // still needs attention rather than every historical mention.
  const [mentionFilter, setMentionFilter] = useState<'all' | 'unread'>('unread');
  const [mentionProjectFilter, setMentionProjectFilter] = useState<string>('');

  // Resolve current user once on mount; sections preference reads from
  // localStorage keyed to that OID so multiple users on the same device
  // don't share collapse state.
  useEffect(() => {
    (async () => {
      const acct = await getAccount(pca);
      const oid = acct?.localAccountId ?? acct?.homeAccountId?.split('.')[0] ?? '';
      setUserId(oid);
      try {
        const raw = window.localStorage.getItem(SECTIONS_KEY(oid));
        if (raw) {
          const parsed = JSON.parse(raw);
          setSections({ ...DEFAULT_SECTIONS, ...parsed });
        }
      } catch {
        // Default already applied — first render uses DEFAULT_SECTIONS.
      }
    })();
  }, [pca]);

  const toggleSection = useCallback((key: keyof SectionsState) => {
    setSections(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        if (userId) window.localStorage.setItem(SECTIONS_KEY(userId), JSON.stringify(next));
      } catch { /* preference write is best-effort */ }
      return next;
    });
  }, [userId]);

  const reload = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const taskFilter =
        `dagrun_pm_assigneeid eq '${userId}' and dagrun_pm_status ne ${TaskStatus.Done} and dagrun_pm_status ne ${TaskStatus.Cancelled}`;
      const [t, p, mFeed, wList] = await Promise.all([
        dvList<Task>(pca, 'dagrun_pm_tasks', {
          select: TASK_FIELDS, filter: taskFilter, top: 200, orderby: 'dagrun_pm_duedate asc',
        }),
        listProjects(pca),
        listMyMentions(pca, userId, 100),
        listMyWatchedItems(pca, userId),
      ]);
      setTasks(t); setProjects(p); setMentions(mFeed); setWatched(wList);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [pca, userId]);

  useEffect(() => { reload(); }, [reload]);

  // Hydrate watched-task entities. Tasks-type watches are the most
  // common; the other entity types (Issue/Risk/Decision/Milestone/CR)
  // would need parallel lookups which we keep minimal here — future
  // expansion when those tabs ship full detail views.
  useEffect(() => {
    if (watched.length === 0) { setWatchedTasks(new Map()); return; }
    const taskIds = watched.filter(w => w.entityType === 'Task').map(w => w.entityId);
    if (taskIds.length === 0) { setWatchedTasks(new Map()); return; }
    let cancelled = false;
    (async () => {
      try {
        const filter = taskIds.map(id => `dagrun_pm_taskid eq ${id}`).join(' or ');
        const rows = await dvList<Task>(pca, 'dagrun_pm_tasks', {
          select: [...TASK_FIELDS, 'modifiedon'],
          filter, top: taskIds.length,
        });
        if (cancelled) return;
        setWatchedTasks(new Map(rows.map(r => [r.dagrun_pm_taskid, r])));
      } catch {
        if (!cancelled) setWatchedTasks(new Map());
      }
    })();
    return () => { cancelled = true; };
  }, [pca, watched]);

  const projectMap = useMemo(() => {
    const m: Record<string, string> = {};
    projects.forEach(p => { m[p.dagrun_pm_projectid] = p.dagrun_pm_name; });
    return m;
  }, [projects]);

  // ── Section 1 — My Tasks (grouped by due proximity) ─────────────────
  const today = useMemo(() => new Date(), []);
  const tasksByGroup = useMemo(() => {
    const buckets: Record<DueGroup, Task[]> = { Overdue: [], 'Due Today': [], 'Due This Week': [], Upcoming: [] };
    tasks.forEach(t => buckets[classifyDue(t, today)].push(t));
    return buckets;
  }, [tasks, today]);
  const visibleDueGroups = DUE_GROUP_ORDER.filter(g => tasksByGroup[g].length > 0);

  // ── Section 2 — My Mentions (aging + filters) ───────────────────────
  // Aging: a mention that has been READ drops off after 30 days. Unread
  // mentions never age out (they still need attention). This aged set is
  // the universe every mention filter / count works from.
  const agedMentions = useMemo(() => {
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return mentions.filter(m => {
      if (!m.isRead) return true;
      const t = m.createdOn ? Date.parse(m.createdOn) : NaN;
      return isNaN(t) ? true : t >= cutoff;
    });
  }, [mentions]);
  const mentionProjects = useMemo(() => {
    const set = new Set<string>();
    agedMentions.forEach(m => { if (m.projectId) set.add(m.projectId); });
    return Array.from(set);
  }, [agedMentions]);
  const filteredMentions = useMemo(() => {
    return agedMentions.filter(m => {
      if (mentionFilter === 'unread' && m.isRead) return false;
      if (mentionProjectFilter && m.projectId !== mentionProjectFilter) return false;
      return true;
    });
  }, [agedMentions, mentionFilter, mentionProjectFilter]);

  // ── Section 3 — My Watch List (group by entity type → project) ──────
  const watchListGrouped = useMemo(() => {
    const groups = new Map<string, WatchedItem[]>();
    watched.forEach(w => {
      const k = w.entityType;
      if (!groups.has(k)) groups.set(k, []);
      (groups.get(k) as WatchedItem[]).push(w);
    });
    // Within each group, sort by last-modified desc when we have the row.
    groups.forEach(arr => {
      arr.sort((a, b) => {
        const ta = watchedTasks.get(a.entityId);
        const tb = watchedTasks.get(b.entityId);
        const ma = ta && (ta as Task & { modifiedon?: string }).modifiedon;
        const mb = tb && (tb as Task & { modifiedon?: string }).modifiedon;
        if (ma && mb) return new Date(mb).getTime() - new Date(ma).getTime();
        return 0;
      });
    });
    return groups;
  }, [watched, watchedTasks]);

  // ── Navigation ──────────────────────────────────────────────────────

  const openTask = (t: Task) => {
    const pid = t._dagrun_pm_projectid_value;
    if (!pid) return;
    nav(`/project/${pid}?taskId=${t.dagrun_pm_taskid}`);
  };

  const openMention = async (m: MentionFeedItem) => {
    if (m.notificationId && !m.isRead && m.projectId && userId) {
      try { await markNotificationRead(pca, m.notificationId, userId, m.projectId); } catch { /* non-fatal */ }
      setMentions(prev => prev.map(x => x.mentionId === m.mentionId ? { ...x, isRead: true } : x));
    }
    if (m.projectId && m.taskId) {
      nav(`/project/${m.projectId}?taskId=${m.taskId}&commentId=${m.commentId}`);
    } else if (m.projectId) {
      nav(`/project/${m.projectId}`);
    }
  };

  const openWatched = (w: WatchedItem) => {
    if (w.entityType === 'Task') {
      const t = watchedTasks.get(w.entityId);
      if (t) {
        openTask(t);
        return;
      }
    }
    // Non-task entity types: navigate to the entity's project context
    // (the parent project record on issues/risks/decisions/etc. lives
    // on each row's _dagrun_pm_projectid_value lookup; without that
    // data hydrated we fall back to the dashboard).
  };

  // ── Render ──────────────────────────────────────────────────────────

  if (loading) return <div className="page"><div className="spinner" style={{ margin: '80px auto' }} /></div>;
  if (error) {
    return (
      <div className="page">
        <div className="card" style={{ padding: 20, color: 'var(--danger)', fontSize: 14 }}>{error}</div>
      </div>
    );
  }

  return (
    <div className="page">
      <h2 style={{ marginBottom: 8 }}>My Dashboard</h2>

      {/* ── SECTION 1 — My Tasks ── */}
      <SectionHeader
        title="My Tasks"
        count={tasks.length}
        collapsed={sections.myTasks}
        onToggle={() => toggleSection('myTasks')}
      />
      {!sections.myTasks && (
        <div style={{ marginBottom: 24 }}>
          {tasks.length === 0 && (
            <div style={{ padding: 20, fontSize: 13, color: 'var(--ink-4)' }}>
              No open tasks assigned to you.
            </div>
          )}
          {visibleDueGroups.map(group => {
            const groupTasks = tasksByGroup[group];
            return (
              <div key={group} style={{ marginBottom: 16 }}>
                <div style={{
                  fontSize: 11, fontWeight: 600, letterSpacing: '0.08em',
                  textTransform: 'uppercase', color: group === 'Overdue' ? 'var(--danger)' : 'var(--ink-4)',
                  margin: '8px 0 6px', fontFamily: 'var(--font-ui)',
                }}>
                  {group} <span style={{ color: 'var(--ink-4)' }}>({groupTasks.length})</span>
                </div>
                {groupTasks.map(t => {
                  const pid = t._dagrun_pm_projectid_value;
                  const projectName = pid ? projectMap[pid] : null;
                  const dueDate = t.dagrun_pm_duedate ? new Date(t.dagrun_pm_duedate) : null;
                  const overdue = dueDate && dueDate < startOfDay(today);
                  const dueColor = overdue ? 'var(--danger)' : isSameDay(dueDate ?? new Date(0), today) ? 'var(--warning)' : 'var(--ink-4)';
                  const priColor = PRIORITY_COLOR[t.dagrun_pm_priority] ?? '#DDE0E6';
                  return (
                    <div
                      key={t.dagrun_pm_taskid}
                      onClick={() => openTask(t)}
                      style={{
                        background: 'var(--surface)',
                        border: '1px solid var(--border-light)',
                        borderLeft: `3px solid ${priColor}`,
                        borderRadius: 'var(--r-md)',
                        padding: '10px 14px',
                        marginBottom: 6,
                        cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 12,
                        transition: 'box-shadow 0.12s, border-color 0.12s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.boxShadow = 'var(--shadow-card)'; }}
                      onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>
                          {t.dagrun_pm_name}
                        </div>
                        {projectName && (
                          <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>
                            {projectName}
                          </div>
                        )}
                      </div>
                      {t.dagrun_pm_status != null && (
                        <span className="pill neutral" style={{ fontSize: 10 }}>
                          {STATUS_LABEL[t.dagrun_pm_status] ?? '—'}
                        </span>
                      )}
                      {t.dagrun_pm_duedate && (
                        <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: dueColor, flexShrink: 0 }}>
                          {formatShortDate(t.dagrun_pm_duedate)}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* ── SECTION 2 — My Mentions ── */}
      <SectionHeader
        title="My Mentions"
        count={agedMentions.length}
        collapsed={sections.myMentions}
        onToggle={() => toggleSection('myMentions')}
      />
      {!sections.myMentions && (
        <div style={{ marginBottom: 24 }}>
          {/* Filter pills row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <FilterPill active={mentionFilter === 'all'} onClick={() => setMentionFilter('all')}>All</FilterPill>
            <FilterPill active={mentionFilter === 'unread'} onClick={() => setMentionFilter('unread')}>Unread</FilterPill>
            <select
              value={mentionProjectFilter}
              onChange={e => setMentionProjectFilter(e.target.value)}
              style={{
                fontFamily: 'var(--font-ui)', fontSize: 12,
                padding: '5px 10px',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-sm)',
                background: 'var(--surface)',
                cursor: 'pointer',
              }}
            >
              <option value="">All projects</option>
              {mentionProjects.map(pid => (
                <option key={pid} value={pid}>{projectMap[pid] ?? pid.slice(0, 8)}</option>
              ))}
            </select>
          </div>

          {filteredMentions.length === 0 && (
            <div style={{ padding: 20, fontSize: 13, color: 'var(--ink-4)' }}>
              No mentions{mentionFilter === 'unread' ? ' unread' : ''}.
            </div>
          )}

          {filteredMentions.map(m => {
            const projectName = m.projectId ? projectMap[m.projectId] : null;
            const author = m.authorName ?? 'Someone';
            return (
              <div
                key={m.mentionId}
                onClick={() => openMention(m)}
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border-light)',
                  borderRadius: 'var(--r-md)',
                  padding: '12px 14px',
                  marginBottom: 8,
                  cursor: 'pointer',
                  transition: 'box-shadow 0.12s, border-color 0.12s',
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = 'var(--shadow-card)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; }}
              >
                {/* Row 1: project name (muted, uppercase, small) */}
                {projectName && (
                  <div style={{
                    fontSize: 10, fontWeight: 600, letterSpacing: '0.08em',
                    textTransform: 'uppercase', color: 'var(--ink-4)',
                    fontFamily: 'var(--font-ui)', marginBottom: 4,
                  }}>{projectName}</div>
                )}
                {/* Row 2: entity title (bold, full size) — task name when
                    resolved, otherwise the entity type as a graceful
                    fallback so the row is never empty. */}
                <div style={{
                  fontSize: 14, fontWeight: 600, color: 'var(--ink)',
                  marginBottom: 6, fontFamily: 'var(--font-ui)',
                }}>
                  {m.entityName ?? m.taskName ?? m.entityType ?? 'Item'}
                </div>
                {/* Row 3: "Author: full body" — wraps naturally, NO truncation. */}
                <div style={{
                  fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.55,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  fontFamily: 'var(--font-ui)',
                }}>
                  <strong style={{ fontWeight: 600 }}>{author}:</strong>{' '}
                  {m.commentBody}
                </div>
                {/* Row 4: timestamp + unread dot */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  marginTop: 8,
                  fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)',
                }}>
                  <span>{relativeTime(m.createdOn)}</span>
                  {!m.isRead && (
                    <span title="Unread" style={{
                      width: 8, height: 8, borderRadius: 4,
                      background: '#0E7490', display: 'inline-block',
                    }} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── SECTION 3 — My Watch List ── */}
      <SectionHeader
        title="My Watch List"
        count={watched.length}
        collapsed={sections.myWatchList}
        onToggle={() => toggleSection('myWatchList')}
      />
      {!sections.myWatchList && (
        <div style={{ marginBottom: 24 }}>
          {watched.length === 0 && (
            <div style={{ padding: 20, fontSize: 13, color: 'var(--ink-4)' }}>
              You aren't watching anything yet.
            </div>
          )}
          {Array.from(watchListGrouped.entries()).map(([typeName, items]) => {
            const chip = TYPE_CHIP[typeName] ?? TYPE_CHIP.Unknown;
            return (
              <div key={typeName} style={{ marginBottom: 14 }}>
                <div style={{
                  fontSize: 11, fontWeight: 600, letterSpacing: '0.08em',
                  textTransform: 'uppercase', color: 'var(--ink-4)',
                  margin: '8px 0 6px', fontFamily: 'var(--font-ui)',
                }}>{typeName} <span style={{ color: 'var(--ink-4)' }}>({items.length})</span></div>
                {items.map(w => {
                  const t = w.entityType === 'Task' ? watchedTasks.get(w.entityId) : null;
                  const projectId = t ? t._dagrun_pm_projectid_value : null;
                  const projectName = projectId ? projectMap[projectId] : null;
                  const modifiedOn = t ? (t as Task & { modifiedon?: string }).modifiedon : null;
                  return (
                    <div
                      key={w.watcherId}
                      onClick={() => openWatched(w)}
                      style={{
                        background: 'var(--surface)',
                        border: '1px solid var(--border-light)',
                        borderRadius: 'var(--r-md)',
                        padding: '10px 14px',
                        marginBottom: 6,
                        cursor: w.entityType === 'Task' && t ? 'pointer' : 'default',
                        display: 'flex', alignItems: 'center', gap: 12,
                        transition: 'box-shadow 0.12s, border-color 0.12s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.boxShadow = 'var(--shadow-card)'; }}
                      onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; }}
                    >
                      <span style={{
                        background: chip.bg, color: chip.fg,
                        padding: '2px 8px', borderRadius: 4,
                        fontSize: 10, fontWeight: 600, letterSpacing: '0.04em',
                        flexShrink: 0,
                      }}>{typeName}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>
                          {t?.dagrun_pm_name ?? `${typeName} ${w.entityId.slice(0, 8)}`}
                        </div>
                        {projectName && (
                          <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>
                            {projectName}
                          </div>
                        )}
                      </div>
                      {t?.dagrun_pm_status != null && (
                        <span className="pill neutral" style={{ fontSize: 10 }}>
                          {STATUS_LABEL[t.dagrun_pm_status] ?? '—'}
                        </span>
                      )}
                      {modifiedOn && (
                        <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--ink-4)', flexShrink: 0 }}>
                          {relativeTime(modifiedOn)}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Filter pill ───────────────────────────────────────────────────────

function FilterPill({ active, onClick, children }: {
  active: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '5px 12px',
        borderRadius: 999,
        border: `1px solid ${active ? 'var(--brand)' : 'var(--border)'}`,
        background: active ? 'var(--brand-mist)' : 'var(--surface)',
        color: active ? 'var(--brand)' : 'var(--ink-3)',
        fontFamily: 'var(--font-ui)', fontSize: 12, fontWeight: active ? 500 : 400,
        cursor: 'pointer',
        transition: 'background 0.12s, color 0.12s, border-color 0.12s',
      }}
    >{children}</button>
  );
}
